import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { AdaptiveAccessControlNode, Header } from "@aptsi-types";
import { AdaptiveAccessNodeService } from "../adaptive-access-node.service";
import { MatDialog } from "@angular/material/dialog";
import { DeleteDialogComponent } from "../../shared/dialogs/delete-dialog/delete-dialog.component";
import { TimeFormatService } from "../../shared/service/time-format.service";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { EnvironmentService } from "src/app/core/service/environment.service";


@Component({
  selector: "app-adaptive-access-node-search",
  templateUrl: "./adaptive-access-node-search.component.html",
  styleUrls: ["./adaptive-access-node-search.component.scss"],
})
export class AdaptiveAccessNodeSearchComponent implements OnInit {
  // ─── Pagination ───────────────────────────────────────────────
  currentPage = 1;
  itemsPerPage = 7;
  totalPages = 0;
  totalPagesNormal = 0;
  totalPagesSearch = 0;

  // ─── Search & Filtering ──────────────────────────────────────
  searchText: string = "";
  filterByHeader: string = "all";
  isSearch = false;
  searchTextChanged = new Subject<string>();

  // ─── Data ─────────────────────────────────────────────────────
  adaptiveaccessnodes: AdaptiveAccessControlNode[] = [];
  filteredData: AdaptiveAccessControlNode[] = [];
  searchedData: AdaptiveAccessControlNode[] = [];

  // ─── UI Configuration ─────────────────────────────────────────
  headers: Header[] = [
    {
      name: "Adaptive Access Node Id",
      property: "adaptiveaccessnodeid",
      showInDropdown: true,
      description: "Unique identifier for the adaptive access node",
    },
    {
      name: "Adaptive Access Node Name",
      property: "adaptiveAccessNodeName",
      showInDropdown: true,
      description: "Name of the adaptive access node",
    },
    {
      name: "Adaptive Access Node Type",
      property: "adaptiveAccessNodeType",
      showInDropdown: true,
      description: "Type or category of the adaptive access node",
    },
    {
      name: "Last Updated",
      property: "timestamp",
      showInDropdown: true,
      description: "Name of the adaptive access node",
    },
  ];

  buttonConfig = {
    update: true,
    delete: true,
    capture: false,
  };

  // ─── Constructor ─────────────────────────────────────────────
  constructor(
    private router: Router,
    private adaptiveAccessControlService: AdaptiveAccessNodeService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private timeFormatService: TimeFormatService,
    public environmentService: EnvironmentService 
  ) {}

  // ─── Lifecycle ───────────────────────────────────────────────
  async ngOnInit(): Promise<void> {

    await this.fetchAdaptiveAccessNodes();
    this.searchTextChanged
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(() => this.searchAdaptiveAccessNodes());

    this.cdr.detectChanges();
  }

  // ─── Getters ─────────────────────────────────────────────────
  get paginatedData(): AdaptiveAccessControlNode[] {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    return this.isSearch
      ? this.searchedData.slice(start, end)
      : this.filteredData.slice(start, end);
  }

  // ─── Event Handlers ──────────────────────────────────────────
  changePage(page: number): void {
    this.currentPage = page;
    this.totalPages = this.isSearch
      ? this.totalPagesSearch
      : this.totalPagesNormal;
    this.isSearch
      ? this.fetchAdaptiveAccessNodesSearch()
      : this.fetchAdaptiveAccessNodes();
  }

  onSearchTextChange(searchValue: string): void {
    this.searchTextChanged.next(searchValue);
  }

  handleAction(event: { action: string; rowIndex: number; rowData: any }): void {
    if (event.action === "update") {
      this.updateAdaptiveAccessNodes(event.rowIndex);
    } else if (event.action === "delete") {
      this.deleteAdaptiveAccessNodes(event.rowIndex);
    }
  }

  // ─── Navigation ──────────────────────────────────────────────
  updateAdaptiveAccessNodes(index: number): void {
    const nodeId = this.isSearch
      ? this.searchedData[index].adaptiveaccessnodeid
      : this.filteredData[index].adaptiveaccessnodeid;

    this.router.navigate([
      "ada-access/auth-tools/adaptiveaccessnode/update",
      nodeId,
    ]);
  }

  createAdaptiveAccessNodes(): void {
    this.router.navigate(["ada-access/auth-tools/adaptiveaccessnode/create"]);
  }

  back(): void {
    if (this.isSearch) {
      this.isSearch = false;
      this.fetchAdaptiveAccessNodes();
    } else {
      this.router.navigate(["ada-access/auth-tools"]);
    }
  }

  // ─── Data Fetching ───────────────────────────────────────────
  async fetchAdaptiveAccessNodes(): Promise<void> {
    try {
      const response = await this.adaptiveAccessControlService
        .getAdaptiveAccessNodes(this.currentPage - 1)
        .toPromise();

      this.filteredData = response.map((node) => ({
        ...node,
        timestamp: this.timeFormatService.formatDate(node.timestamp),
      }));

      this.updatePagination(response);
    } catch (error) {
      console.error("Error fetching adaptive access nodes:", error);
    }
  }

  async fetchAdaptiveAccessNodesSearch(): Promise<void> {
    try {
      const response = await this.adaptiveAccessControlService
        .getAdaptiveAccessNodesSearch(
          this.currentPage - 1,
          this.searchText,
          this.filterByHeader
        )
        .toPromise();

      this.searchedData = response.map((node) => ({
        ...node,
        timestamp: this.timeFormatService.formatDate(node.timestamp),
      }));

      this.updatePagination(response);
    } catch (error) {
      console.error("Error fetching access node search:", error);
    }
  }

  private updatePagination(response: any[]): void {
    if (response.length > 0 && response[0].totalPages) {
      const totalPages = response[0].totalPages;
      const currentPage = response[0].currentPage + 1;

      if (this.isSearch) {
        this.totalPagesSearch = totalPages;
      } else {
        this.totalPagesNormal = totalPages;
      }

      this.totalPages = totalPages;
      this.currentPage = currentPage;
    }
  }

  // ─── Search ──────────────────────────────────────────────────
  // async searchAdaptiveAccessNodes(): Promise<void> {
  //   try {
  //     if (this.searchText) {
  //       this.isSearch = true;
        
  //       await this.fetchAdaptiveAccessNodesSearch();
  //       this.totalPages = this.totalPagesSearch;

  //       const selectedHeader = this.headers.find(
  //         (h) => h.property === this.filterByHeader && h.showInDropdown
  //       );

  //       if (selectedHeader) {
  //         this.searchedData = this.searchedData
  //           .filter((node) => {
  //             const value = node[selectedHeader.property];
  //             return value
  //               ?.toString()
  //               .toLowerCase()
  //               .includes(this.searchText.toLowerCase());
  //           })
  //           .sort((a, b) =>
  //             a[selectedHeader.property]
  //               ?.toString()
  //               .localeCompare(b[selectedHeader.property]?.toString())
  //           );
  //       } else {
  //         this.filteredData = [];
  //       }
  //     } else {
  //       this.isSearch = false;
       
  //       await this.fetchAdaptiveAccessNodes();
  //     }
  //   } catch (error) {
  //     console.error("Error during search:", error);
  //   }
  // }

   async searchAdaptiveAccessNodes(): Promise<void> {

    try {

      if (this.searchText?.trim()) {

        this.isSearch = true;
        this.currentPage = 1;

        if (!this.filterByHeader) {
          this.filterByHeader = 'all';
        }

        // Backend handles:
        // search
        // pagination
        // filtering
        await this.fetchAdaptiveAccessNodesSearch();

      } else {

        this.isSearch = false;
        this.currentPage = 1;

        await this.fetchAdaptiveAccessNodes();
      }

    } catch (error) {

      console.error(
        'Error during search:',
        error
      );
    }
  }

  // ─── Delete ──────────────────────────────────────────────────
  async deleteAdaptiveAccessNodes(index: number): Promise<void> {
    try {
      const dialogRef = this.dialog.open(DeleteDialogComponent, {
        data: {
          title: "Are you sure?",
          message: "Do you really want to delete this Adaptive Access Node?",
          isConfirm: false,
        },
      });

      const result = await dialogRef.afterClosed().toPromise();
      if (!result) return;

      const nodeId = this.isSearch
        ? this.searchedData[index].adaptiveaccessnodeid
        : this.filteredData[index].adaptiveaccessnodeid;

      await this.adaptiveAccessControlService
        .deleteadaptiveaccessnodes(nodeId)
        .toPromise();

      this.isSearch
        ? this.fetchAdaptiveAccessNodesSearch()
        : this.fetchAdaptiveAccessNodes();

      this.dialog.open(DeleteDialogComponent, {
        data: {
          title: "Successful",
          message: "The Adaptive Access Node has been deleted successfully.",
          isConfirm: true,
        },
      });
    } catch (error) {
      console.error("Error deleting Adaptive Access Node:", error);
    }
  }
}
